import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { PayoutInvoiceApi } from "../../../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../../../services/organizations/selectors";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";

interface ListState {
	resource?: Record<string, any>;
	partnerId: number;
}

const listName = "PAYOUT_INVOICE";
const resourceType = "PAYOUT_INVOICE";

const List = ({ resource, partnerId }: ListState) => {
	const { t } = useTranslation("admin");
	const { addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const organization = useSelector(selectOrganization);
	const partner = useSelector(selectPartner);
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;

	if (!resource) return null;
	const data = resource.read();

	const onRemoveAttachment = async (id: string) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await partnerApi.removePayoutInvoice(partnerId, id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	let config = {
		fields: [
			{
				id: "attachment",
				disableSorting: true,
				name: `${t("modules.payoff.field.attachment.title")}`,
				render: (item: PayoutInvoiceApi) => (
					<a
						rel="noreferrer"
						href={`${item.url}?access_token=${localStorage.getItem("token")}`}
						target="_blank"
					>
						{item.name}
					</a>
				),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: PayoutInvoiceApi) => `${FormatDate(item.created_at)}`,
			},
			{
				id: "issued_at",
				name: `${t("modules.payoff.field.issued_at.title")}`,
				type: "date",
				render: (item: PayoutInvoiceApi) => `${FormatDate(item.issued_at)}`,
			},
			{
				id: "number",
				name: `${t("lib:common.word.number")}`,
				type: "text",
				render: (item: PayoutInvoiceApi) => item.number,
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					NEW: t("enums.common.status.NEW"),
					CONFIRMED: t("enums.common.status.CONFIRMED"),
					DENIED: t("enums.common.status.DENIED"),
					PAID: t("enums.common.status.PAID"),
				},
				render: (item: PayoutInvoiceApi) => <FormatResourceStatus status={item.status} />,
				renderExport: (item: PayoutInvoiceApi) => t(`enums.common.status.${item.status}`),
			},
			{
				id: "total_price",
				name: `${t("common.word.total_price")}`,
				type: "number",
				render: (item: PayoutInvoiceApi) =>
					FormatMoney({
						amount: item?.total_price?.amount,
						currency: item?.total_price?.currency,
					}),
			},
		],
		filters: [],
		selectedColumns: ["attachment", "created_at", "issued_at", "number", "status", "total_price"],
		actions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: PayoutInvoiceApi) => onRemoveAttachment(item.id),
				visible: (item: PayoutInvoiceApi) => item.status === "NEW",
				refreshListAfter: true,
			},
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return partnerApi.getPayoutInvoices(partnerId, params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.payout_invoice.header.title"),
			filename: t("modules.payout_invoice.field.export_config.filename"),
			organization: `${organization.name}`,
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	const buttons: ButtonProps[] = [
		{
			variant: "primary",
			title: t("common.action.add", { ns: "lib" }),
			path: `/partnership/${partner?.id}/payout_invoices/new`,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/partnership/${partner?.id}/payout_invoices/new`,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.payout_invoice.header.title")} buttons={buttons} />}
			<ListData config={config} mobileActions={mobileActions} />
		</>
	);
};

const PartnerPreviewPayoutInvoicesIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const partnerId = useSelector(selectPartner)?.id;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const segmentContextValue = React.useContext(SegmentContext);
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleChangeTabTitle(
			`${t("modules.payout_invoice.header.title")} | ${t("modules.partner.field.partner_panel.title")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List partnerId={Number(partnerId)} resource={resource} />
		</Suspense>
	);
};

export default PartnerPreviewPayoutInvoicesIndexPage;
